import { Howl } from 'howler';
import { TimelineEngine } from '@xzdarcy/react-timeline-editor';
import { getAssetDownloadUrl } from '../../services/assetUrlUtils';

class AudioControl {
  cacheMap: Record<string, Howl> = {};
  // SAS token for the current video, used to resolve stored relative blob
  // paths (e.g. `videos/<id>/assets/foo.mp3`) into playable URLs at playback
  // time — mirrors how the canvas resolves background assets.
  sasToken?: string | null;

  setSasToken(token?: string | null) {
    this.sasToken = token;
  }
  listenerMap: Record<
    string,
    {
      time?: (data: { time: number }) => void;
      rate?: (data: { rate: number }) => void;
      engine?: TimelineEngine;
    }
  > = {};

  start(data: {
    id: string;
    engine: TimelineEngine;
    src: string;
    startTime: number;
    time: number;
  }) {
    const { id, src, startTime, time, engine } = data;
    const playableSrc = getAssetDownloadUrl(src, this.sasToken);
    let item: Howl;
    if (this.cacheMap[id]) {
      item = this.cacheMap[id];
      item.rate(engine.getPlayRate());
      item.seek((time - startTime) % item.duration());
      item.play();
    } else {
      item = new Howl({ src: playableSrc, loop: true, autoplay: true });
      this.cacheMap[id] = item;
      item.on('load', () => {
        item.rate(engine.getPlayRate());
        item.seek((time - startTime) % item.duration());
      });
    }

    const timeListener = (data: { time: number }) => {
      const { time } = data;
      item.seek(time);
    };
    const rateListener = (data: { rate: number }) => {
      const { rate } = data;
      item.rate(rate);
    };
    if (!this.listenerMap[id]) this.listenerMap[id] = {};
    engine.on('afterSetTime', timeListener);
    engine.on('afterSetPlayRate', rateListener);
    this.listenerMap[id].time = timeListener;
    this.listenerMap[id].rate = rateListener;
    this.listenerMap[id].engine = engine;
  }

  stop(data: { id: string; engine: TimelineEngine }) {
    const { id, engine } = data;
    if (this.cacheMap[id]) {
      const item = this.cacheMap[id];
      item.stop();
      if (this.listenerMap[id]) {
        this.listenerMap[id].time &&
          engine.off('afterSetTime', this.listenerMap[id].time);
        this.listenerMap[id].rate &&
          engine.off('afterSetPlayRate', this.listenerMap[id].rate);
        delete this.listenerMap[id];
      }
    }
  }

  /**
   * Stop every cached sound (looped SFX keep playing on their own) and detach
   * all engine listeners. Called when leaving the editor so no audio survives
   * navigation away mid-play. Best-effort: never throws.
   */
  stopAll() {
    for (const [id, item] of Object.entries(this.cacheMap)) {
      try {
        item.stop();
      } catch {
        // Best-effort teardown must never break unmount.
      }
      try {
        item.unload();
      } catch {
        // Best-effort teardown must never break unmount.
      }
      const listeners = this.listenerMap[id];
      const engine = listeners?.engine;
      try {
        if (engine && listeners?.time) engine.off('afterSetTime', listeners.time);
        if (engine && listeners?.rate) engine.off('afterSetPlayRate', listeners.rate);
      } catch {
        // Best-effort teardown must never break unmount.
      }
      delete this.listenerMap[id];
    }
    this.cacheMap = {};
  }
}

export default new AudioControl();
